/**
 * Time Windowing V2 - First Principles Approach
 * 
 * Shows 60 minutes total:
 * - 15 minutes of past context (fading)
 * - 45 minutes of future preview (half full, half fading)
 * 
 * All gradients are continuous and mathematically proven.
 */

import { TaskWithState } from '../types';

export interface WindowSegment {
  task: TaskWithState;
  visibleStartMin: number;  // 0-60 (float allowed for precision)
  visibleEndMin: number;    // 0-60 (float allowed for precision)
  alphaStart: number;       // 0.0-1.0 (gradient start opacity)
  alphaEnd: number;         // 0.0-1.0 (gradient end opacity)
  radius: number;           // wedge radius
  startMs: number;          // Absolute start time of this segment
  endMs: number;            // Absolute end time of this segment
}

const PAST_WINDOW_MINS = 15;
const FUTURE_WINDOW_BASE_MINS = 45;

/**
 * Get current minute from Date object with float precision (including seconds/ms)
 */
function getCurrentMinuteFloat(time: Date): number {
  return time.getMinutes() + (time.getSeconds() / 60) + (time.getMilliseconds() / 60000);
}

/**
 * Add minutes with wrapping (handles 0-60 boundary)
 */
function minuteAdd(base: number, offset: number): number {
  return (base + offset + 60) % 60;
}

/**
 * Calculate difference between minutes (handles wrapping)
 */
function minuteDiff(end: number, start: number): number {
  let diff = end - start;
  if (diff < 0) diff += 60;
  return diff;
}

/**
 * Calculate alpha for a minute in the past window
 * Formula: (minute - windowStart) / 15
 * Result: 0.0 at T-15, 1.0 at T
 */
function calculatePastAlpha(minute: number, windowStart: number): number {
  // Calculate how many minutes from window start
  let diff = minute - windowStart;
  
  // Handle wrapping
  // If the difference is large negative, it means we wrapped around 0/60
  // e.g. minute=5, start=55 => diff=-50. We want +10.
  // If the difference is small negative, it means we are just slightly behind due to float precision
  // e.g. minute=54.999, start=55.000 => diff=-0.001. We want 0.
  
  // Use a threshold (30 min) to distinguish wrapping from precision errors
  if (diff < -30) {
    diff += 60;
  }
  
  // Clamp negative values (precision errors) to 0
  if (diff < 0) diff = 0;
  
  const alpha = diff / PAST_WINDOW_MINS;
  const clamped = Math.min(1.0, Math.max(0.0, alpha));
  
  return clamped;
}

/**
 * Calculate alpha for a minute in the future window
 * First half: alpha = 1.0
 * Second half: alpha fades from 1.0 to 0.0
 */
function calculateFutureAlpha(
  minute: number,
  currentMinute: number,
  halfPoint: number
): number {
  const distanceFromNow = minuteDiff(minute, currentMinute);
  
  if (distanceFromNow <= halfPoint) {
    return 1.0; // Full zone
  } else {
    // Fade zone
    const progress = (distanceFromNow - halfPoint) / halfPoint;
    return Math.max(0.0, 1.0 - progress);
  }
}

/**
 * Helper to generate segments for a future task (or future portion of current task)
 * Handles:
 * 1. Truncating to max future window (45 min)
 * 2. Splitting at fade boundary (22.5 min)
 * 3. Calculating alphas
 */
function processFutureSegment(
    task: TaskWithState,
    startTimeMs: number,
    endTimeMs: number,
    currentMs: number,
    currentMinute: number,
    fadeStartMinute: number,
    halfPoint: number,
    maxFutureMs: number
): { segments: WindowSegment[], durationUsed: number } {
    const segments: WindowSegment[] = [];

    // 1. Truncate to max future window
    // Calculate effective end time (truncating to window max)
    const isTruncated = endTimeMs > maxFutureMs;
    
    const segmentEndMs = isTruncated ? maxFutureMs : endTimeMs;
    const segmentEndMin = getCurrentMinuteFloat(new Date(segmentEndMs));
    const segmentStartMin = getCurrentMinuteFloat(new Date(startTimeMs));

    // Calculate effective duration in minutes
    const effectiveDuration = (segmentEndMs - startTimeMs) / (60 * 1000);
    
    // Safety check for tiny segments
    if (effectiveDuration <= 0.01) {
        return { segments: [], durationUsed: 0 };
    }

    // Calculate distances from NOW (in minutes)
    const startDist = (startTimeMs - currentMs) / (60 * 1000);
    const endDist = (segmentEndMs - currentMs) / (60 * 1000);

    // 2. Determine segments based on fade boundary
    if (endDist <= halfPoint) {
        // Case A: Entirely in Full Zone (0 - 22.5m)
        segments.push({
            task,
            visibleStartMin: segmentStartMin,
            visibleEndMin: segmentEndMin,
            alphaStart: 1.0,
            alphaEnd: 1.0,
            radius: 185,
            startMs: startTimeMs,
            endMs: segmentEndMs
        });
    } else if (startDist >= halfPoint) {
        // Case B: Entirely in Fade Zone (22.5m - 45m)
        const alphaStart = calculateFutureAlpha(segmentStartMin, currentMinute, halfPoint);
        const alphaEnd = calculateFutureAlpha(segmentEndMin, currentMinute, halfPoint);
        
        segments.push({
            task,
            visibleStartMin: segmentStartMin,
            visibleEndMin: segmentEndMin,
            alphaStart,
            alphaEnd,
            radius: 185,
            startMs: startTimeMs,
            endMs: segmentEndMs
        });
    } else {
        // Case C: Crosses Fade Boundary -> Split
        const fadeStartMs = currentMs + (halfPoint * 60 * 1000);
        
        // Part 1: Full Zone (Start -> FadeStart)
        segments.push({
            task,
            visibleStartMin: segmentStartMin,
            visibleEndMin: fadeStartMinute,
            alphaStart: 1.0,
            alphaEnd: 1.0,
            radius: 185,
            startMs: startTimeMs,
            endMs: fadeStartMs
        });
        
        // Part 2: Fade Zone (FadeStart -> End)
        const alphaEnd = calculateFutureAlpha(segmentEndMin, currentMinute, halfPoint);
        
        segments.push({
            task,
            visibleStartMin: fadeStartMinute,
            visibleEndMin: segmentEndMin,
            alphaStart: 1.0,
            alphaEnd,
            radius: 185,
            startMs: fadeStartMs,
            endMs: segmentEndMs
        });
    }

    return { segments, durationUsed: effectiveDuration };
}

/**
 * Main function: Calculate time window segments
 */
export function calculateTimeWindow(
  allTasks: TaskWithState[],
  currentTime: Date
): WindowSegment[] {
  const result: WindowSegment[] = [];
  const currentMinute = getCurrentMinuteFloat(currentTime);
  const currentMs = currentTime.getTime();
  
  // Define time window
  const windowStart = minuteAdd(currentMinute, -PAST_WINDOW_MINS);
  
  // Sort tasks by start time, then ID for stability
  const sortedTasks = [...allTasks].sort((a, b) => {
    const timeDiff = a.startTime.getTime() - b.startTime.getTime();
    if (timeDiff !== 0) return timeDiff;
    return a.id.localeCompare(b.id);
  });
  
  // Find ALL active tasks
  let currentTask: TaskWithState | null = null;
  for (const task of sortedTasks) {
    if (task.startTime.getTime() <= currentMs && task.endTime.getTime() > currentMs) {
      currentTask = task;
      break; // Pick the first one
    }
  }
  
  // STEP 1: Process past tasks (fill up to 15 minutes)
  let pastMinutesUsed = 0;
  
  // Define past window in absolute time
  const pastWindowStartMs = currentMs - (PAST_WINDOW_MINS * 60 * 1000);
  
  // Get tasks that ended before current time AND ended after past window start
  const pastTasks = sortedTasks
    .filter(task => {
      const endMs = task.endTime.getTime();
      return endMs <= currentMs && endMs > pastWindowStartMs;
    })
    .sort((a, b) => {
      const timeDiff = b.endTime.getTime() - a.endTime.getTime(); // Most recent first
      if (timeDiff !== 0) return timeDiff;
      return a.id.localeCompare(b.id);
    });
  
  // Scaling factor for completed tasks
  const COMPLETED_ALPHA_FACTOR = 0.9;

  for (const task of pastTasks) {
    if (pastMinutesUsed >= PAST_WINDOW_MINS) break;
    
    const taskEndMs = task.endTime.getTime();
    const taskStartMs = task.startTime.getTime();
    
    // Calculate actual visible time range in milliseconds
    const visibleStartMs = Math.max(taskStartMs, pastWindowStartMs);
    const visibleEndMs = taskEndMs;
    
    // Convert to duration in minutes (float)
    const durationMins = (visibleEndMs - visibleStartMs) / (60 * 1000);
    
    if (durationMins <= 0.01) continue; 
    
    // Check if we have space
    const remainingSpace = PAST_WINDOW_MINS - pastMinutesUsed;
    
    // Determine effective start based on remaining space
    let effectiveStartMs = visibleStartMs;
    if (durationMins > remainingSpace + 0.01) {
        effectiveStartMs = visibleEndMs - (remainingSpace * 60 * 1000);
    }

    const effectiveStartMin = getCurrentMinuteFloat(new Date(effectiveStartMs));
    const effectiveEndMin = getCurrentMinuteFloat(new Date(visibleEndMs));
    
    const alphaStart = calculatePastAlpha(effectiveStartMin, windowStart) * COMPLETED_ALPHA_FACTOR;
    const alphaEnd = calculatePastAlpha(effectiveEndMin, windowStart) * COMPLETED_ALPHA_FACTOR;
    
    result.push({
      task,
      visibleStartMin: effectiveStartMin,
      visibleEndMin: effectiveEndMin,
      alphaStart,
      alphaEnd,
      radius: 185,
      startMs: effectiveStartMs,
      endMs: visibleEndMs
    });
    
    pastMinutesUsed += (visibleEndMs - effectiveStartMs) / (60 * 1000);
    if (pastMinutesUsed >= PAST_WINDOW_MINS) break;
  }
  
  // STEP 2: Process current task (if exists)
  let futureMinutesUsed = 0;
  
  // Calculate shared future constants
  const totalFutureSpace = FUTURE_WINDOW_BASE_MINS;
  const maxFutureMs = currentMs + (totalFutureSpace * 60 * 1000);
  const halfPoint = totalFutureSpace / 2;
  const fadeStartMinute = minuteAdd(currentMinute, halfPoint);

  if (currentTask) {
    const taskStartMs = currentTask.startTime.getTime();
    const taskEndMs = currentTask.endTime.getTime();
    
    // Past portion (already happened)
    if (taskStartMs < currentMs) {
      const pastPortionStartMs = Math.max(taskStartMs, pastWindowStartMs);
      const pastPortionEndMs = currentMs;
      const durationMins = (pastPortionEndMs - pastPortionStartMs) / (60 * 1000);
      
      if (durationMins > 0.01) {
        const pastPortionStartMin = getCurrentMinuteFloat(new Date(pastPortionStartMs));
        const pastPortionEndMin = currentMinute;
        
        const alphaStart = calculatePastAlpha(pastPortionStartMin, windowStart);
        const alphaEnd = 1.0; 
        
        result.push({
          task: currentTask,
          visibleStartMin: pastPortionStartMin,
          visibleEndMin: pastPortionEndMin,
          alphaStart,
          alphaEnd,
          radius: 185,
          startMs: pastPortionStartMs,
          endMs: pastPortionEndMs
        });
        
        pastMinutesUsed += durationMins;
      }
    }
    
    // Future portion (yet to happen)
    if (taskEndMs > currentMs) {
        // Use shared helper
        const processed = processFutureSegment(
            currentTask,
            currentMs, // Start of future portion is NOW
            taskEndMs,
            currentMs,
            currentMinute,
            fadeStartMinute,
            halfPoint,
            maxFutureMs
        );
        result.push(...processed.segments);
        futureMinutesUsed += processed.durationUsed;
    }
  }
  
  // STEP 5: Process future tasks (not yet started)
  const futureTasks = sortedTasks.filter(task => {
    const taskStartMs = task.startTime.getTime();
    // Only include tasks that start within the future window
    return taskStartMs > currentMs && taskStartMs < maxFutureMs;
  });
  
  for (const task of futureTasks) {
    if (futureMinutesUsed >= totalFutureSpace) break;
    
    const taskStartMs = task.startTime.getTime();
    const taskEndMs = task.endTime.getTime();

    // Use shared helper
    const processed = processFutureSegment(
        task,
        taskStartMs,
        taskEndMs,
        currentMs,
        currentMinute,
        fadeStartMinute,
        halfPoint,
        maxFutureMs
    );
    
    // Check if we need to truncate based on REMAINING space (logic from before)
    // Actually, processFutureSegment truncates to maxFutureMs, which is the ABSOLUTE limit.
    // So if a task starts at T+40 and ends at T+50, processFutureSegment handles it (truncates to T+45).
    // The only additional constraint is if previous tasks somehow filled up the space disjointly?
    // No, tasks are sorted by time. So we just fill forward.
    // The only case is if futureMinutesUsed exceeds limit? 
    // processFutureSegment handles "Start > maxFutureMs" by returning empty (checked by startDist calc inside? No, checked by caller filter).
    // So we just add segments.
    
    result.push(...processed.segments);
    futureMinutesUsed += processed.durationUsed;
  }
  
  return result;
}
